import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type CancelToken } from "axios";

import { useAuth, type UseAuthResponse } from "../auth/useAuth";

async function doRequest(
  axios: AxiosInstance,
  config: AxiosRequestConfig,
  cancelToken: CancelToken,
  auth?: UseAuthResponse
) {
  const token = auth?.isAuthenticated ? await auth.getToken() : null;
  return axios({
    ...config,
    cancelToken,
    headers: {
      ...(token ? { Authorization: `Bearer ${token.accessToken}` } : {}),
      // eslint-disable-next-line typescript-eslint/no-misused-spread
      ...config.headers,
    },
  });
}

// eslint-disable-next-line typescript-eslint/no-unsafe-assignment
const apiEndpoint = process.env.STAVERTON_API_ENDPOINT;

if (!apiEndpoint) {
  throw new Error("STAVERTON_API_ENDPOINT is not set");
}

// eslint-disable-next-line typescript-eslint/no-unsafe-assignment
axios.defaults.baseURL = apiEndpoint;

// eslint-disable-next-line typescript-eslint/no-unsafe-assignment
export const AXIOS_INSTANCE = axios.create({ baseURL: apiEndpoint });

export const useCustomInstance = <T>(): ((config: AxiosRequestConfig) => Promise<AxiosResponse<T>>) => {
  const auth = useAuth();
  // eslint-disable-next-line typescript-eslint/promise-function-async
  return (config: AxiosRequestConfig) => {
    const source = axios.CancelToken.source();
    const promise = doRequest(AXIOS_INSTANCE, config, source.token, auth);

    // @ts-expect-error Cancel doesn't exist on promise by default
    promise.cancel = () => {
      source.cancel("Query was cancelled by React Query");
    };

    return promise;
  };
};

export const useUnauthenticatedInstance = <T>(): ((config: AxiosRequestConfig) => Promise<AxiosResponse<T>>) => {
  // eslint-disable-next-line typescript-eslint/promise-function-async
  return (config: AxiosRequestConfig) => {
    const source = axios.CancelToken.source();
    const promise = doRequest(AXIOS_INSTANCE, config, source.token);

    // @ts-expect-error Cancel doesn't exist on promise by default
    promise.cancel = () => {
      source.cancel("Query was cancelled by React Query");
    };

    return promise;
  };
};

export default useCustomInstance;
